import React from "react";
import { Platform, Pressable, StyleSheet, type ViewStyle } from "react-native";
import { Modal } from "react-native-paper";

import { useEscapeToDismiss } from "../hooks/useEscapeToDismiss";

type AppModalProps = React.ComponentProps<typeof Modal> & {
  /**
   * Centre the children over the whole screen. Paper's Surface then fills the
   * screen and hides its backdrop, so an outside catcher sits behind the card
   * to keep click-outside-to-close working.
   */
  centered?: boolean;
};

/**
 * react-native-paper's Modal plus Escape-to-dismiss on web. Paper only wires
 * the Android back button, so on web an Escape press left the modal (and its
 * full-screen backdrop over the header) open.
 */
const AppModal: React.FC<AppModalProps> = ({ centered, ...props }) => {
  const { visible, dismissable = true, onDismiss } = props;
  useEscapeToDismiss(visible && dismissable, onDismiss);
  if (!centered) return <Modal {...props} />;
  return (
    <Modal
      {...props}
      contentContainerStyle={[styles.centered, props.contentContainerStyle]}
    >
      <Pressable
        accessible={false}
        testID="modal-outside"
        disabled={!dismissable}
        onPress={dismissable ? onDismiss : undefined}
        style={[StyleSheet.absoluteFill, webDefaultCursor]}
      />
      {props.children}
    </Modal>
  );
};

// A Pressable shows a pointer on web; the outside area should not.
const webDefaultCursor = (
  Platform.OS === "web" ? { cursor: "auto" } : null
) as ViewStyle | null;

const styles = StyleSheet.create({
  centered: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    paddingHorizontal: 20,
  },
});

export default AppModal;
